import { faPalette } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { AdvancedSelect, Button, Modal } from '@orobix/orobix-fe-components'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { v4 as uuidv4 } from 'uuid'

import { get, update } from '../../utils/api'
import { AnnotationLabel, Color } from '../Annotator/types'
import useStyles from './EditLabelModal.styles'

type SelectOption = {
  value: string
  label: string
  color: Color
}

interface EditLabelModalProps {
  productId: number
  onLabelUpdated?: (labels: Array<AnnotationLabel>) => void
}

const EditLabelModal = ({ productId, onLabelUpdated }: EditLabelModalProps) => {
  const { t } = useTranslation()
  const classes = useStyles()

  const [isOpen, setOpen] = useState(false)

  const [labels, setLabels] = useState<Array<AnnotationLabel>>([])
  const [updatedLabels, setUpdatedLabels] = useState<Array<AnnotationLabel>>([])
  const [colorOptions, setColorOptions] = useState<Array<SelectOption>>([])

  const [isLoadingLabels, setLoadingLabels] = useState(false)
  const [isLoadingColors, setLoadingColors] = useState(false)

  const fetchLabels = () => {
    setLoadingLabels(true)
    return get(`/labels/?product=${productId}`)
      .then((data: any) => {
        setLabels(data)
        setUpdatedLabels(data)
      })
      .finally(() => setLoadingLabels(false))
  }

  const fetchColors = () => {
    setLoadingColors(true)
    return get('/colors/')
      .then((data: any) => {
        setColorOptions(
          data.map((color: Color) => ({
            color,
            value: color.name,
            label: (
              <>
                <span
                  style={{ color: color.code }}
                  className={classes.colorSwatch}
                >
                  ■
                </span>
                <span>{color.name}</span>
              </>
            ),
          }))
        )
      })
      .finally(() => setLoadingColors(false))
  }

  const handleSave = () => {
    setLoadingLabels(true)
    Promise.all(
      updatedLabels.map((label) =>
        update(`/labels/${label.id}/`, {
          color_id: label.color.id,
        })
      )
    )
      .then(() => {
        toast.success(t('Labels updated'))
        if (onLabelUpdated) {
          onLabelUpdated(updatedLabels)
        }
      })
      .catch(() => {
        toast.error(t('Error updating labels'))
      })
      .finally(() => {
        setLoadingLabels(false)
        setOpen(false)
      })
  }

  return (
    <>
      <Button
        icon={<FontAwesomeIcon icon={faPalette} />}
        label={`${t('Edit colors')}`}
        title={`${t('Edit colors')}`}
        onClick={() => {
          setOpen(true)
          fetchLabels()
          fetchColors()
        }}
        hideLabel
        size="xs"
        type="blank-default"
        buttonType="button"
      />
      {isOpen && (
        <Modal
          size="sm"
          onCloseRequest={() => {
            setOpen(false)
          }}
          title={t('Choose label colors')}
          footer={
            <Button
              onClick={() => {
                handleSave()
              }}
              label={t('Save')}
              type="action"
              buttonType="button"
              loading={isLoadingLabels || isLoadingColors}
              disabled={isLoadingLabels || isLoadingColors}
            />
          }
        >
          <ul>
            {labels.map((label, index) => (
              <div key={uuidv4()}>
                <span key={`${index.toString()}`} className={classes.bold}>
                  {label.name}
                </span>
                <AdvancedSelect
                  key={`${label.id}`}
                  // label={label.name}
                  value={colorOptions.filter(
                    (option) => option.value === label.color.name
                  )}
                  name="color"
                  id="color"
                  options={colorOptions}
                  onChange={(value) => {
                    if (value) {
                      const newLabels = [...updatedLabels]
                      newLabels[index].color = value.color
                      setUpdatedLabels(newLabels)
                    }
                  }}
                />
              </div>
            ))}
          </ul>
        </Modal>
      )}
    </>
  )
}

export default EditLabelModal
